import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State
} from '@stencil/core';
import { TranslationUtils } from '../../../shared/TranslationUtils';
import { ButtonSize, ButtonVariant } from '../../jui-button/jui-button.model';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';
import {
  JuiCameraFacing,
  JuiImageClick,
  JuiImageEntry,
  JuiImageRejection,
  JuiImageSort
} from './jui-image-capture.model';
import {
  checkFile,
  createImageId,
  dataUrlBytes,
  pickerAccept,
  placeImages,
  remainingSlots,
  resizeImage
} from './image-capture.utils';

interface ImageCaptureI18ns {
  uploadImage: string;
  addImage: string;
  remove: string;
  removeImage: string;
  imageFailed: string;
}

@Component({
  tag: 'jui-image-capture',
  styleUrl: 'jui-image-capture.scss',
  shadow: true
})
export class JuiImageCapture implements ComponentInterface {
  @Element() el: HTMLElement;

  /**
   * The images shown in the control, oldest or newest first according to `sort`. Stored images
   * must already have a displayable `src`; the control never fetches.
   */
  @Prop({ mutable: true }) images: JuiImageEntry[] = [];

  /**
   * The control label.
   */
  @Prop() label: string = null;

  /**
   * The capture button text. Defaults to a translated "Upload image".
   */
  @Prop() actionlabel: string = null;

  /**
   * The maximum number of images. Null means no limit; 1 makes the control single image.
   */
  @Prop() maximages: number = null;

  /**
   * Accepted file types, as for the native `accept` attribute.
   */
  @Prop() accept = 'image/*';

  /**
   * Maximum size in bytes of a picked file, or of an image added through `addImages`.
   */
  @Prop() maxfilesize = 20000000;

  /**
   * Longest edge in pixels that picked images are resized to.
   */
  @Prop() maxedge = 4000;

  /**
   * JPEG quality, between 0 and 1, of resized images.
   */
  @Prop() quality = 0.93;

  /**
   * Which camera the native picker should open on mobile. Null leaves the choice to the device.
   */
  @Prop() capture: JuiCameraFacing = null;

  /**
   * Where new images are placed: 'asc' appends, 'desc' puts the newest first.
   */
  @Prop() sort: JuiImageSort = 'asc';

  /**
   * Disables capture and removal.
   */
  @Prop() disabled = false;

  /**
   * Shows the images only, with no capture button and no remove bar.
   */
  @Prop() readonly = false;

  /**
   * Prefix for the data-qa hooks on the button, hidden input and remove buttons.
   */
  @Prop() dataqastring: string = null;

  /**
   * Id for the hidden file input, so tests and labels can reach it.
   */
  @Prop() inputid: string = null;

  /**
   * Emitted with the full image list after any capture or removal.
   */
  @Event({ composed: true }) juiImagesChange: EventEmitter<JuiImageEntry[]>;

  /**
   * Emitted for each file or image the control refuses, separately from any validation.
   */
  @Event({ composed: true }) juiReject: EventEmitter<JuiImageRejection>;

  /**
   * Emitted when the capture button is pressed, before the picker opens. Cancel it to supply
   * the image another way, then call `addImages` or `openPicker`.
   */
  @Event({ composed: true, cancelable: true }) juiCaptureRequest: EventEmitter<void>;

  /**
   * Emitted when an image is clicked or, in read-only mode, activated from the keyboard.
   */
  @Event({ composed: true }) juiImageClick: EventEmitter<JuiImageClick>;

  @State() private busy = false;

  @State() private imagesWithVerdict: string[] = [];

  private i18ns: ImageCaptureI18ns;

  componentWillLoad(): void {
    const translations = TranslationUtils.instance;
    this.i18ns = {
      uploadImage: translations.translate('_commons.uploadImage', 'Upload image'),
      addImage: translations.translate('_commons.addImage', 'Add image'),
      remove: translations.translate('_commons.remove', 'Remove'),
      removeImage: translations.translate('_commons.removeImage', 'Remove image'),
      imageFailed: translations.translate('_commons.imageFailed', 'Image could not be shown')
    };
  }

  /**
   * Adds images that were captured outside the control, such as from a camera, as JPEG data
   * URLs. They are checked against `maxfilesize` and `maximages` but not resized again.
   */
  @Method()
  async addImages(srcs: string[]): Promise<void> {
    const accepted: JuiImageEntry[] = [];
    for (const src of srcs) {
      if (remainingSlots(this.images.length + accepted.length, this.maximages) === 0) {
        this.juiReject.emit({ fileName: null, reason: 'max-images' });
        continue;
      }
      if (dataUrlBytes(src) > this.maxfilesize) {
        this.juiReject.emit({ fileName: null, reason: 'file-too-large' });
        continue;
      }
      accepted.push({ id: createImageId(), src, state: 'ready' });
    }
    this.commit(accepted);
  }

  /**
   * Opens the native file picker without emitting `juiCaptureRequest`.
   */
  @Method()
  async openPicker(): Promise<void> {
    this.fileInput()?.click();
  }

  private fileInput(): HTMLInputElement | null {
    return this.el.shadowRoot.querySelector('input[type="file"]');
  }

  private requestCapture(): void {
    const request = this.juiCaptureRequest.emit();
    if (!request.defaultPrevented) {
      this.fileInput()?.click();
    }
  }

  private async handleFilesPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) {
      return;
    }

    this.busy = true;
    try {
      this.commit(await this.resizeAccepted(files));
    } finally {
      this.busy = false;
    }
  }

  private async resizeAccepted(files: File[]): Promise<JuiImageEntry[]> {
    const accepted: JuiImageEntry[] = [];
    for (const file of files) {
      if (remainingSlots(this.images.length + accepted.length, this.maximages) === 0) {
        this.juiReject.emit({ fileName: file.name, reason: 'max-images' });
        continue;
      }
      const rejection = checkFile(file, this.accept, this.maxfilesize);
      if (rejection) {
        this.juiReject.emit(rejection);
        continue;
      }
      try {
        const src = await resizeImage(file, { maxEdgePx: this.maxedge, quality: this.quality });
        accepted.push({ id: createImageId(), src, state: 'ready' });
      } catch {
        this.juiReject.emit({ fileName: file.name, reason: 'decode-failed' });
      }
    }
    return accepted;
  }

  private commit(added: JuiImageEntry[]): void {
    if (added.length === 0) {
      return;
    }
    this.images = placeImages(this.images, added, this.sort);
    this.juiImagesChange.emit(this.images);
  }

  private remove(id: string): void {
    this.images = this.images.filter(image => image.id !== id);
    this.juiImagesChange.emit(this.images);
  }

  private qa(suffix: string): string | null {
    return this.dataqastring ? `${this.dataqastring}${suffix}` : null;
  }

  private clickImage(image: JuiImageEntry, index: number): void {
    this.juiImageClick.emit({ id: image.id, index });
  }

  private activateImage(event: KeyboardEvent, image: JuiImageEntry, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.clickImage(image, index);
    }
  }

  private verdictChanged(id: string, slot: HTMLSlotElement): void {
    const hasVerdict = slot.assignedElements().length > 0;
    if (hasVerdict === this.imagesWithVerdict.includes(id)) {
      return;
    }
    this.imagesWithVerdict = hasVerdict
      ? [...this.imagesWithVerdict, id]
      : this.imagesWithVerdict.filter(existing => existing !== id);
  }

  private renderImage(image: JuiImageEntry, index: number) {
    const alt = `${this.label ?? this.i18ns.uploadImage} ${index + 1}`;
    const showActions = !this.disabled && !this.readonly;
    return (
      <li
        class={{
          'jui-image-capture__item': true,
          'jui-image-capture__item--with-verdict': this.readonly && this.imagesWithVerdict.includes(image.id)
        }}
        data-qa={this.qa(`_image_${index}`)}
      >
        <slot
          name={`image-${image.id}`}
          onSlotchange={event => this.verdictChanged(image.id, event.target as HTMLSlotElement)}
        />
        <div
          class={{
            'jui-image-capture__photo': true,
            'jui-image-capture__photo--placeholder': image.state !== 'ready',
            'jui-image-capture__photo--with-actions': showActions
          }}
        >
          {showActions && (
            <div class="jui-image-capture__actions">
              <div class="jui-image-capture__extra-actions">
                <slot name={`image-${image.id}-actions`} />
              </div>
              <jui-button
                class="jui-image-capture__remove"
                size={ButtonSize.sm}
                color={ButtonVariant.low}
                aria-label={`${this.i18ns.removeImage} ${index + 1}`}
                data-qa={this.qa(`_remove_${index}`)}
                onClick={() => this.remove(image.id)}
              >
                {this.i18ns.remove}
              </jui-button>
            </div>
          )}
          <div
            class="jui-image-capture__frame"
            role={this.readonly && image.state === 'ready' ? 'button' : null}
            tabIndex={this.readonly && image.state === 'ready' ? 0 : null}
            aria-label={this.readonly && image.state === 'ready' ? alt : null}
            data-qa={this.qa(`_frame_${index}`)}
            onClick={() => image.state === 'ready' && this.clickImage(image, index)}
            onKeyDown={event => this.readonly && image.state === 'ready' && this.activateImage(event, image, index)}
          >
            {image.state === 'loading' && <jui-skeleton-loader class="jui-image-capture__media" height="300px" />}
            {image.state === 'ready' && <img class="jui-image-capture__media" src={image.src} alt={alt} />}
            {image.state === 'error' && (
              <div
                class="jui-image-capture__media jui-image-capture__media--error"
                role="img"
                aria-label={this.i18ns.imageFailed}
              >
                <jui-icon name="broken_image" isButton={false} color={IconVariant.low} size={IconSize.lg} />
              </div>
            )}
            {image.state === 'ready' && (
              <div class="jui-image-capture__overlay">
                <slot name={`image-${image.id}-overlay`} />
              </div>
            )}
          </div>
        </div>
      </li>
    );
  }

  render() {
    const hasImages = this.images.length > 0;
    const full = remainingSlots(this.images.length, this.maximages) === 0;
    const buttonText = this.actionlabel ?? (hasImages ? this.i18ns.addImage : this.i18ns.uploadImage);

    return (
      <Host>
        <div class="jui-image-capture">
          {this.label && <label class="jui-image-capture__label">{this.label}</label>}
          {!full && !this.readonly && (
            <jui-button
              class="jui-image-capture__action"
              expand
              size={ButtonSize.lg}
              loading={this.busy}
              disabled={this.disabled || this.busy}
              aria-busy={this.busy ? 'true' : null}
              data-qa={this.qa('_button')}
              onClick={() => this.requestCapture()}
            >
              <jui-icon
                slot="icon"
                name={hasImages ? 'add_a_photo' : 'photo_camera'}
                isButton={false}
                color={IconVariant.current}
                size={IconSize.sm}
              />
              {buttonText}
            </jui-button>
          )}
          {!this.readonly && (
            <input
              class="jui-image-capture__input"
              id={this.inputid ?? undefined}
              type="file"
              hidden
              accept={pickerAccept(this.accept)}
              multiple={this.maximages !== 1}
              capture={this.capture ?? undefined}
              data-qa={this.qa('')}
              onChange={event => this.handleFilesPicked(event)}
            />
          )}
          {hasImages && (
            <ul class="jui-image-capture__list">{this.images.map((image, index) => this.renderImage(image, index))}</ul>
          )}
        </div>
      </Host>
    );
  }
}
